import { arrayEq } from '../utilities/equality';
import DebugService from 'app/shared/debug.service';

function logError(...args) {
    DebugService.log('AppState', ...args);
    DebugService.trace('AppState');
}

export class AppStateContainer {
    _state: any;
    _keyDetails: any;
    _effectsCanMutateKeys: any;

    constructor(keyDetails, effectsCanMutateKeys) {
        this._state = {};
        this._keyDetails = {};
        this._effectsCanMutateKeys = effectsCanMutateKeys;
        this.addKeys(keyDetails);
    }

    getState() {
        return Object.assign({}, this._state);
    }

    addKeys(keyDetails) {
        Object.keys(keyDetails).forEach((key) => {
            if (this._state[key] !== undefined) {
                throw new Error(`State key conflict: '${key}'`);
            }

            const initialValue = keyDetails[key].initialValue;

            if (
                initialValue === undefined ||
                (keyDetails[key].isValid && !keyDetails[key].isValid(initialValue))
            ) {
                this._state[key] = keyDetails[key].defaultValue;
            } else {
                this._state[key] = initialValue;
            }

            this._keyDetails[key] = {
                isEqual: keyDetails[key].isEqual,
                isValid: keyDetails[key].isValid,
            };
        });
    }

    updateState(updateFn) {
        /**
         *
         *  This method must be synchronous.
         *  The running of the update function, which produces a new state,
         *  needs to be persisted immediately (synchronously)!
         *
         *  If you don't do this then this can be the order of events
         *
         *      state 1 = {a, b}
         *      run effect to remove A (state {a, b} to {b}) produces state 2
         *      await keyword is hit and browser yields to next microtask
         *      run effect to remove B (state {a, b} to {a}) produces state 3
         *      await keyword is hit and browser yields to next microtask
         *      await 1 returns and persists state 2
         *      await 2 returns and persists state 3
         *
         *
         *  The exact problem here is an effect can run in between another effect running and that effect's state
         *  being set as the current state, meaning the 2nd effect runs with an incorrect state.
         */
        const oldState = this.getState();
        let newState = updateFn(oldState);

        if (!newState) {
            throw new Error('Did not produce a new state');
        }

        const keys = Object.keys(newState);

        if (!this._effectsCanMutateKeys) {
            if (!arrayEq(Object.keys(oldState), keys)) {
                throw new Error('Added or removed a state key');
            }
        }

        const changedKeys = new Set();

        keys.forEach((key) => {
            const keyDetails = this._keyDetails[key] || {};
            const isEqual = keyDetails.isEqual || ((a, b) => a === b);
            const newValue = newState[key];

            if (!isEqual(oldState[key], newValue)) {
                const isValid = keyDetails.isValid;
                if (isValid && !isValid(newValue)) {
                    logError(newValue, `is not a valid state value for '${key}'`);
                    throw new Error(`Key '${key}' did not pass validation`);
                }
                changedKeys.add(key);
                this._set(key, newValue);
            }
        });

        return { changedKeys, oldState, newState };
    }

    // TODO computed properties using a system similar to:
    // https://github.com/FormidableLabs/freactal/blob/master/src/state.js
    _set(key, value) {
        this._state[key] = value;
    }
}
